import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { FormField } from '../../../components/ui/FormField';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useCancelReceipt } from '../network/dues.api';

const MIN_REASON_LEN = 20;

interface CancelReceiptModalProps {
  receiptId: string;
  receiptNumber: string;
  isSameDay: boolean;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CancelReceiptModal({
  receiptId,
  receiptNumber,
  isSameDay,
  open,
  onClose,
  onSuccess,
}: CancelReceiptModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const mutation = useCancelReceipt();

  const handleSubmit = async () => {
    setError('');
    if (reason.trim().length < MIN_REASON_LEN) {
      setError(`Reason must be at least ${MIN_REASON_LEN} characters`);
      return;
    }
    try {
      await mutation.mutateAsync({ receiptId, reason: reason.trim() });
      onSuccess();
    } catch (e: unknown) {
      const error = e as { message?: string };
      setError(error?.message ?? 'Cancellation failed');
      console.error(e);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel receipt {receiptNumber}</DialogTitle>
          <DialogDescription>
            {isSameDay
              ? 'Same-day cancellation: the due will revert immediately.'
              : 'Next-day cancellation: this will be routed to the Approvals queue (RECEIPT_REVERSAL). The due reverts only after a checker approves.'}
          </DialogDescription>
        </DialogHeader>

        <FormField
          label="Reason"
          required
          htmlFor="cancel-reason"
          hint={`${reason.trim().length} / ${MIN_REASON_LEN} characters minimum`}
          error={error || undefined}
        >
          <textarea
            id="cancel-reason"
            rows={3}
            className={`w-full px-3 py-2 bg-surface-3 border rounded-[6px] text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 resize-none ${
              error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : 'border-border focus:border-accent-500 focus:ring-accent-500'
            }`}
            placeholder="State the reason for cancellation…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Back
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            loading={mutation.isPending}
            disabled={reason.trim().length < MIN_REASON_LEN}
          >
            {isSameDay ? 'Cancel receipt' : 'Submit for approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const _schemesEmpty = () => <EmptyState title="No data" description="No data found." icon={<span />} />;
